import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { formatEUR } from '../../lib/format';

interface DocLine {
  number: string;
  ttc: number;
  recap?: string | null;
  deductedOn?: string | null;
}
interface OrderCredit {
  customer: {
    creditLimit: number | null;
    unlimited: boolean;
    exposure: number;
    manualRelease: number;
    available: number | null;
  };
  order: { onAccount: number; reserved: number; settled: number; released: number };
  monthly: {
    deliveryNotes: DocLine[];
    creditNotes: DocLine[];
    deposits: DocLine[];
    nextNetTTC: number;
  };
}

/**
 * "Account credit" for an on-account customer (order-sheet prototype). Every figure is the
 * server's (GET /orders/:id/credit): the customer's limit, exposure and headroom; what this order
 * draws on the account and where that stands; and which of its documents are already on a
 * monthly invoice and what it still adds to the next one. Nothing renders for a cash customer.
 */
export function OrderCreditPanel({ orderId, refreshKey }: { orderId: string; refreshKey: number }) {
  const { t } = useTranslation();
  const [c, setC] = useState<OrderCredit | null>(null);

  useEffect(() => {
    let alive = true;
    apiClient
      .get<{ data: OrderCredit | null }>(`/orders/${orderId}/credit`)
      .then((r) => {
        if (alive) setC(r.data.data);
      })
      .catch(() => {
        if (alive) setC(null);
      });
    return () => {
      alive = false;
    };
  }, [orderId, refreshKey]);

  if (!c) return null;
  const row = (label: string, value: string, strong = false) => (
    <>
      <span className="text-muted-foreground">{label}</span>
      <span
        className={strong ? 'text-right font-semibold tabular-nums' : 'text-right tabular-nums'}
      >
        {value}
      </span>
    </>
  );
  const docs = [
    ...c.monthly.deliveryNotes.map((d) => ({ ...d, kind: 'bl' as const })),
    ...c.monthly.creditNotes.map((d) => ({ ...d, kind: 'avo' as const })),
    ...c.monthly.deposits.map((d) => ({ ...d, kind: 'dep' as const })),
  ];

  return (
    <section className="border rounded p-4 bg-card">
      <h2 className="font-semibold mb-2">{t('orders.credit.title')}</h2>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
          {row(
            t('orders.credit.limit'),
            c.customer.unlimited
              ? t('orders.credit.unlimited')
              : formatEUR(c.customer.creditLimit ?? 0)
          )}
          {row(t('orders.credit.exposure'), formatEUR(c.customer.exposure))}
          {c.customer.manualRelease > 0 &&
            row(t('orders.credit.manualRelease'), formatEUR(c.customer.manualRelease))}
          {row(
            t('orders.credit.available'),
            c.customer.available == null ? '—' : formatEUR(c.customer.available),
            true
          )}
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
          {row(t('orders.credit.onAccount'), formatEUR(c.order.onAccount))}
          {row(t('orders.credit.reserved'), formatEUR(c.order.reserved))}
          {row(t('orders.credit.settled'), formatEUR(c.order.settled))}
          {c.order.released > 0 && row(t('orders.credit.released'), formatEUR(c.order.released))}
        </div>
      </div>
      {docs.length > 0 && (
        <div className="mt-3 border-t pt-2 text-sm">
          <div className="mb-1 font-medium">{t('orders.credit.monthly')}</div>
          <ul className="space-y-0.5 text-xs">
            {docs.map((d) => {
              const on = d.recap ?? d.deductedOn ?? null;
              return (
                <li key={d.number} className="flex justify-between gap-3">
                  <span>
                    <span className="font-mono">{d.number}</span>{' '}
                    <span className="text-muted-foreground">
                      {on
                        ? t('orders.credit.onRecap', { number: on })
                        : t('orders.credit.nextRecap')}
                    </span>
                  </span>
                  <span className="tabular-nums">
                    {d.kind === 'bl' ? '' : '−'}
                    {formatEUR(d.ttc)}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-1 flex justify-between font-semibold">
            <span>{t('orders.credit.nextNet')}</span>
            <span className="tabular-nums">{formatEUR(c.monthly.nextNetTTC)}</span>
          </div>
        </div>
      )}
    </section>
  );
}
